import { useEffect, useState, useRef, useMemo } from "react";
import { Navigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import {
  Plus, Loader2, X, Dumbbell, Trophy, Flame, Play, Pause, RotateCcw, Timer, ChevronDown, Check,
} from "lucide-react";
import { toast } from "sonner";
import { UNITS, getUnit, setUnit, lbToKg, kgToLb } from "@/lib/units";

const TIMER_PRESETS = [60, 90, 120, 180, 240];

// --- Rest timer hook -------------------------------------------------------
function useRestTimer() {
  const [total, setTotal] = useState(0);
  const [remaining, setRemaining] = useState(0);
  const [running, setRunning] = useState(false);
  const intervalRef = useRef(null);
  const audioCtxRef = useRef(null);

  const beep = () => {
    try {
      if (!audioCtxRef.current) {
        audioCtxRef.current = new (window.AudioContext || window.webkitAudioContext)();
      }
      const ctx = audioCtxRef.current;
      const o = ctx.createOscillator();
      const g = ctx.createGain();
      o.connect(g); g.connect(ctx.destination);
      o.type = "sine"; o.frequency.value = 880;
      g.gain.setValueAtTime(0.001, ctx.currentTime);
      g.gain.exponentialRampToValueAtTime(0.4, ctx.currentTime + 0.01);
      g.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 0.6);
      o.start(); o.stop(ctx.currentTime + 0.6);
    } catch { /* ignore */ }
  };

  const tick = () => {
    setRemaining((r) => {
      if (r <= 1) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
        setRunning(false);
        // double-beep at end
        beep();
        setTimeout(beep, 300);
        if (navigator.vibrate) navigator.vibrate([200, 100, 200]);
        return 0;
      }
      return r - 1;
    });
  };

  const start = (seconds) => {
    if (intervalRef.current) clearInterval(intervalRef.current);
    setTotal(seconds);
    setRemaining(seconds);
    setRunning(true);
    intervalRef.current = setInterval(tick, 1000);
  };
  const pause = () => {
    if (intervalRef.current) clearInterval(intervalRef.current);
    intervalRef.current = null;
    setRunning(false);
  };
  const resume = () => {
    if (remaining > 0 && !intervalRef.current) {
      setRunning(true);
      intervalRef.current = setInterval(tick, 1000);
    }
  };
  const reset = () => {
    if (intervalRef.current) clearInterval(intervalRef.current);
    intervalRef.current = null;
    setRunning(false);
    setRemaining(0);
    setTotal(0);
  };
  const addSeconds = (s) => {
    setRemaining((r) => Math.max(0, r + s));
    if (s > 0 && !intervalRef.current) {
      setRunning(true);
      intervalRef.current = setInterval(tick, 1000);
    }
  };

  useEffect(() => () => {
    if (intervalRef.current) clearInterval(intervalRef.current);
  }, []);

  return { total, remaining, running, start, pause, resume, reset, addSeconds };
}

const fmtTime = (sec) => {
  const m = Math.floor(sec / 60);
  const s = sec % 60;
  return `${m}:${String(s).padStart(2, "0")}`;
};


// --- Rest timer floating bar ----------------------------------------------
function RestTimerBar({ timer, defaultRest, setDefaultRest }) {
  const { remaining, total, running, start, pause, resume, reset, addSeconds } = timer;
  const pct = total > 0 ? ((total - remaining) / total) * 100 : 0;
  const lowTime = remaining > 0 && remaining <= 10;

  if (remaining === 0) {
    return (
      <div className="brutal-border bg-[#0a0a0a] p-3 flex items-center gap-3 flex-wrap" data-testid="rest-timer-idle">
        <Timer className="w-4 h-4 text-white/50" />
        <div className="font-mono text-[10px] tracking-widest text-white/40">REST</div>
        <div className="flex items-center gap-1 flex-wrap">
          {TIMER_PRESETS.map((s) => (
            <button
              key={s}
              onClick={() => { setDefaultRest(s); start(s); }}
              data-testid={`timer-preset-${s}`}
              className={`px-3 py-1 font-mono text-xs border transition-colors ${
                defaultRest === s
                  ? "border-[#FF3333] text-[#FF3333]"
                  : "border-white/15 text-white/60 hover:border-white/40 hover:text-white"
              }`}
            >
              {fmtTime(s)}
            </button>
          ))}
        </div>
        <div className="ml-auto font-mono text-[9px] tracking-widest text-white/30">AUTO-STARTS AFTER EACH SET</div>
      </div>
    );
  }

  return (
    <div className={`brutal-border bg-[#0a0a0a] p-4 sticky top-16 z-40 relative ${lowTime ? "animate-pulse" : ""}`} data-testid="rest-timer-active">
      <div className="absolute inset-x-0 bottom-0 h-1 bg-white/5">
        <div
          className="h-full transition-all duration-1000"
          style={{
            width: `${pct}%`,
            background: lowTime ? "#FF3333" : "#00E5FF",
            boxShadow: lowTime ? "0 0 10px #FF3333" : "0 0 10px #00E5FF",
          }}
        />
      </div>
      <div className="flex items-center gap-4 flex-wrap">
        <Timer className={`w-5 h-5 ${lowTime ? "text-[#FF3333]" : "text-[#00E5FF]"}`} />
        <div>
          <div className="font-mono text-[9px] tracking-widest text-white/40">REST</div>
          <div
            data-testid="rest-timer-display"
            className={`font-heading text-3xl md:text-4xl leading-none tabular-nums ${lowTime ? "text-[#FF3333]" : "text-white"}`}
          >
            {fmtTime(remaining)}
          </div>
        </div>
        <div className="flex items-center gap-1 ml-auto flex-wrap">
          <button
            onClick={() => addSeconds(-15)}
            data-testid="timer-minus-15"
            className="font-mono text-xs border border-white/15 hover:border-white/40 text-white/70 hover:text-white px-2 py-1.5"
          >
            -15s
          </button>
          <button
            onClick={() => addSeconds(15)}
            data-testid="timer-plus-15"
            className="font-mono text-xs border border-white/15 hover:border-white/40 text-white/70 hover:text-white px-2 py-1.5"
          >
            +15s
          </button>
          {running ? (
            <button onClick={pause} data-testid="timer-pause" className="font-mono text-xs border border-white/15 text-white px-2 py-1.5 flex items-center gap-1">
              <Pause className="w-3 h-3" /> Pause
            </button>
          ) : (
            <button onClick={resume} data-testid="timer-resume" className="font-mono text-xs border border-[#00E5FF] text-[#00E5FF] px-2 py-1.5 flex items-center gap-1">
              <Play className="w-3 h-3" /> Resume
            </button>
          )}
          <button onClick={reset} data-testid="timer-reset" className="font-mono text-xs border border-white/15 hover:border-[#FF3333] text-white/70 hover:text-[#FF3333] px-2 py-1.5 flex items-center gap-1">
            <RotateCcw className="w-3 h-3" /> Skip
          </button>
        </div>
      </div>
    </div>
  );
}


// --- Per-exercise card -----------------------------------------------------
function ExerciseCard({ name, sets, unit, defaultRest, onLogSet, onRemoveSet, displayWeight }) {
  const lastSet = sets[sets.length - 1];
  const initWeight = lastSet
    ? unit === "lb"
      ? String(Math.round(kgToLb(lastSet.weight_kg) * 10) / 10)
      : String(lastSet.weight_kg)
    : "";
  const initReps = lastSet ? String(lastSet.reps) : "";

  const [weight, setWeight] = useState(initWeight);
  const [reps, setReps] = useState(initReps);
  const [rpe, setRpe] = useState("");
  const [logging, setLogging] = useState(false);
  const [restOverride, setRestOverride] = useState(defaultRest);

  /* eslint-disable */
  useEffect(() => {
    // When a new set lands, prefill from the latest set for quick repeat
    if (lastSet) {
      setWeight(unit === "lb"
        ? String(Math.round(kgToLb(lastSet.weight_kg) * 10) / 10)
        : String(lastSet.weight_kg));
      setReps(String(lastSet.reps));
    }
  }, [sets.length, unit]);
  /* eslint-enable */

  const submit = async (e) => {
    e?.preventDefault();
    if (!weight || !reps || logging) return;
    setLogging(true);
    try {
      await onLogSet({
        exercise: name,
        weightInput: parseFloat(weight),
        reps: parseInt(reps, 10),
        rpe: rpe ? parseFloat(rpe) : null,
        restSeconds: restOverride,
      });
      setRpe("");
    } finally {
      setLogging(false);
    }
  };

  const bestSet = sets.reduce((a, b) => (a && a.est_1rm_kg > b.est_1rm_kg ? a : b), null);
  const isPR = bestSet?.new_pr;

  return (
    <div className="brutal-border bg-[#0a0a0a]" data-testid={`exercise-card-${name}`}>
      <div className="flex items-center justify-between flex-wrap gap-2 px-4 py-3 border-b border-white/5">
        <div className="flex items-center gap-3">
          <Dumbbell className="w-4 h-4 text-[#FF3333]" />
          <div className="font-heading uppercase text-lg leading-none">{name}</div>
          <div className="font-mono text-[10px] text-white/40 tracking-widest">{sets.length} SET{sets.length === 1 ? "" : "S"}</div>
        </div>
        <div className="flex items-center gap-2">
          {isPR && (
            <div className="flex items-center gap-1 font-mono text-[10px] tracking-widest text-[#FFB800]">
              <Trophy className="w-3 h-3" /> PR
            </div>
          )}
          <RestPresetMenu value={restOverride} onChange={setRestOverride} />
        </div>
      </div>

      {/* Existing sets */}
      {sets.length > 0 && (
        <div className="px-3 py-2 space-y-1">
          <div className="grid grid-cols-12 gap-2 font-mono text-[9px] tracking-widest text-white/30 px-1">
            <div className="col-span-1">SET</div>
            <div className="col-span-3">WEIGHT</div>
            <div className="col-span-2">REPS</div>
            <div className="col-span-2">RPE</div>
            <div className="col-span-3">e1RM</div>
          </div>
          {sets.map((s, i) => (
            <div key={s.id} className="grid grid-cols-12 gap-2 items-center px-1 py-1.5 font-mono text-sm border-t border-white/5" data-testid={`set-row-${s.id}`}>
              <div className="col-span-1 text-white/40">#{i + 1}</div>
              <div className="col-span-3 text-white tabular-nums">{displayWeight(s.weight_kg)}</div>
              <div className="col-span-2 text-white/80">{s.reps}</div>
              <div className="col-span-2 text-white/40">{s.rpe ? `${s.rpe}` : "—"}</div>
              <div className="col-span-3 text-white/50 text-xs tabular-nums">{displayWeight(s.est_1rm_kg)}</div>
              <div className="col-span-1 flex justify-end">
                <button onClick={() => onRemoveSet(s.id)} className="text-white/30 hover:text-[#FF3333]" data-testid={`set-remove-${s.id}`}>
                  <X className="w-3.5 h-3.5" />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Add set */}
      <form onSubmit={submit} className="grid grid-cols-12 gap-2 px-3 py-3 border-t border-white/10 bg-black/40">
        <input
          type="number" step="0.5" inputMode="decimal"
          value={weight} onChange={(e) => setWeight(e.target.value)}
          placeholder={`Wt ${unit}`}
          data-testid={`set-weight-${name}`}
          className="col-span-4 bg-transparent border border-white/15 focus:border-[#FF3333] outline-none px-3 py-2 font-sub text-sm"
        />
        <input
          type="number" inputMode="numeric"
          value={reps} onChange={(e) => setReps(e.target.value)}
          placeholder="Reps"
          data-testid={`set-reps-${name}`}
          className="col-span-3 bg-transparent border border-white/15 focus:border-[#FF3333] outline-none px-3 py-2 font-sub text-sm"
        />
        <input
          type="number" step="0.5" inputMode="decimal"
          value={rpe} onChange={(e) => setRpe(e.target.value)}
          placeholder="RPE"
          data-testid={`set-rpe-${name}`}
          className="col-span-3 bg-transparent border border-white/15 focus:border-[#FF3333] outline-none px-3 py-2 font-sub text-sm"
        />
        <button
          type="submit"
          disabled={logging || !weight || !reps}
          data-testid={`set-log-${name}`}
          className="col-span-2 btn-forged-red text-xs px-2 disabled:opacity-40"
        >
          {logging ? <Loader2 className="w-3 h-3 animate-spin" /> : <><Check className="w-4 h-4" /></>}
        </button>
      </form>
    </div>
  );
}

function RestPresetMenu({ value, onChange }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="relative">
      <button
        type="button"
        onClick={() => setOpen(!open)}
        className="flex items-center gap-1 px-2 py-1 border border-white/15 font-mono text-[10px] tracking-widest text-white/70 hover:text-white hover:border-white/40"
      >
        <Timer className="w-3 h-3" /> {fmtTime(value)} <ChevronDown className="w-3 h-3" />
      </button>
      {open && (
        <div className="absolute right-0 mt-1 z-30 bg-black brutal-border w-32">
          {TIMER_PRESETS.map((s) => (
            <button
              key={s}
              onClick={() => { onChange(s); setOpen(false); }}
              className={`w-full text-left px-3 py-2 font-mono text-xs transition-colors ${
                value === s ? "text-[#FF3333]" : "text-white/70 hover:text-white hover:bg-white/5"
              }`}
            >
              {fmtTime(s)}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}


// --- Main page -------------------------------------------------------------
export default function WorkoutTracker() {
  const { user, loading: authLoading } = useAuth();
  const [log, setLog] = useState(null);
  const [loading, setLoading] = useState(true);
  const [unit, setU] = useState(() => getUnit(UNITS.WEIGHT, "lb"));
  const [defaultRest, setDefaultRest] = useState(90);
  const [newExercise, setNewExercise] = useState("");
  // Exercises shown but not yet logged (added by the user this session)
  const [pendingExercises, setPendingExercises] = useState([]);
  const today = new Date().toISOString().slice(0, 10);
  const timer = useRestTimer();

  const switchUnit = (u) => { setU(u); setUnit(UNITS.WEIGHT, u); };
  const displayWeight = (kg) =>
    unit === "lb" ? `${Math.round(kgToLb(kg) * 10) / 10} lb` : `${kg} kg`;

  const reload = async () => {
    const { data } = await api.get("/workouts/log");
    setLog(data);
  };

  useEffect(() => {
    if (authLoading || !user) return;
    reload().finally(() => setLoading(false));
    // eslint-disable-next-line
  }, [user, authLoading]);

  // Build the active exercise list (logged today + freshly added)
  const exerciseList = useMemo(() => {
    if (!log) return [];
    const loggedNames = Object.keys(log.groups);
    const merged = [...loggedNames];
    for (const p of pendingExercises) {
      if (!merged.some((n) => n.toLowerCase() === p.toLowerCase())) merged.push(p);
    }
    return merged;
  }, [log, pendingExercises]);

  if (authLoading) return null;
  if (!user) return <Navigate to="/auth" replace />;

  const addExercise = (name) => {
    const v = (name || newExercise).trim();
    if (!v) return;
    setPendingExercises((p) => p.some((x) => x.toLowerCase() === v.toLowerCase()) ? p : [...p, v]);
    setNewExercise("");
  };

  const logSet = async ({ exercise, weightInput, reps, rpe, restSeconds }) => {
    try {
      const kg = unit === "lb" ? lbToKg(weightInput) : weightInput;
      const { data } = await api.post("/workouts/log", {
        exercise, weight_kg: kg, reps, rpe,
      });
      if (data.new_pr) {
        const prDisplay = unit === "lb"
          ? `${Math.round(kgToLb(data.est_1rm_kg) * 10) / 10} lb`
          : `${data.est_1rm_kg} kg`;
        toast.success(`NEW PR · ${exercise} · est ${prDisplay}`);
      }
      await reload();
      timer.start(restSeconds || defaultRest);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not log set");
    }
  };

  const removeSet = async (entryId) => {
    try {
      await api.delete(`/workouts/log/${entryId}`);
      await reload();
    } catch {
      toast.error("Could not delete");
    }
  };

  if (loading || !log) {
    return <div className="py-32 flex justify-center"><Loader2 className="w-8 h-8 animate-spin text-[#FF3333]" /></div>;
  }

  const totalSets = Object.values(log.groups).reduce((s, arr) => s + arr.length, 0);
  const totalVolume = Object.values(log.groups).reduce(
    (s, arr) => s + arr.reduce((a, e) => a + e.weight_kg * e.reps, 0), 0,
  );
  const displayVolume = unit === "lb" ? kgToLb(totalVolume) : totalVolume;

  return (
    <div className="max-w-4xl mx-auto px-4 md:px-8 py-8" data-testid="workout-page">
      <div className="flex items-center justify-between flex-wrap gap-3 mb-6">
        <div>
          <div className="font-mono text-[10px] tracking-[0.4em] text-[#FF3333]">FORGE · {today}</div>
          <h1 className="font-heading uppercase text-4xl md:text-5xl leading-[0.9] mt-2">Workout Log</h1>
        </div>
        <div className="inline-flex border border-white/15">
          {[{v:"lb",l:"LB"},{v:"kg",l:"KG"}].map((o) => (
            <button
              key={o.v} onClick={() => switchUnit(o.v)}
              data-testid={`workout-unit-${o.v}`}
              className={`px-3 py-1.5 font-mono text-[10px] tracking-widest transition-colors ${
                unit === o.v ? "bg-[#FF3333] text-black" : "text-white/60 hover:text-white"
              }`}
            >{o.l}</button>
          ))}
        </div>
      </div>

      {/* Quick stats */}
      <div className="grid grid-cols-3 gap-2 mb-4">
        <div className="brutal-border bg-[#0a0a0a] p-3">
          <div className="font-mono text-[9px] tracking-widest text-white/40">SETS</div>
          <div className="font-heading text-2xl mt-0.5">{totalSets}</div>
        </div>
        <div className="brutal-border bg-[#0a0a0a] p-3">
          <div className="font-mono text-[9px] tracking-widest text-white/40">EXERCISES</div>
          <div className="font-heading text-2xl mt-0.5">{exerciseList.length}</div>
        </div>
        <div className="brutal-border bg-[#0a0a0a] p-3">
          <div className="font-mono text-[9px] tracking-widest text-white/40">VOLUME ({unit.toUpperCase()})</div>
          <div className="font-heading text-2xl mt-0.5">{Math.round(displayVolume).toLocaleString()}</div>
        </div>
      </div>

      {/* Rest timer */}
      <RestTimerBar timer={timer} defaultRest={defaultRest} setDefaultRest={setDefaultRest} />

      {/* Planned exercises chips */}
      {log.planned?.length > 0 && (
        <div className="mt-4 mb-2" data-testid="planned-section">
          <div className="font-mono text-[10px] tracking-[0.3em] text-[#00E5FF] mb-2 flex items-center gap-2">
            <Flame className="w-3 h-3" /> TODAY&apos;S PLAN — TAP TO ADD
          </div>
          <div className="flex flex-wrap gap-2">
            {log.planned.map((ex) => {
              const already = exerciseList.some((n) => n.toLowerCase() === ex.name.toLowerCase());
              return (
                <button
                  key={ex.name}
                  onClick={() => addExercise(ex.name)}
                  disabled={already}
                  data-testid={`planned-${ex.name}`}
                  className={`px-3 py-1.5 border text-xs font-sub transition-colors ${
                    already
                      ? "border-white/5 text-white/30 line-through"
                      : "border-[#00E5FF]/30 text-[#00E5FF] hover:bg-[#00E5FF]/10"
                  }`}
                >
                  + {ex.name} <span className="font-mono text-[9px] text-white/40 ml-1">{ex.sets}×{ex.reps}</span>
                </button>
              );
            })}
          </div>
        </div>
      )}

      {/* Exercises */}
      <div className="mt-6 space-y-4">
        {exerciseList.map((name) => (
          <ExerciseCard
            key={name}
            name={name}
            sets={log.groups[name] || []}
            unit={unit}
            defaultRest={defaultRest}
            onLogSet={logSet}
            onRemoveSet={removeSet}
            displayWeight={displayWeight}
          />
        ))}
        {exerciseList.length === 0 && (
          <div className="brutal-border bg-[#0a0a0a]/40 p-10 text-center font-sub text-white/40">
            No exercises yet. Tap a planned chip above, or add your own below.
          </div>
        )}
      </div>

      {/* Add exercise */}
      <form
        onSubmit={(e) => { e.preventDefault(); addExercise(); }}
        className="mt-6 flex gap-2"
        data-testid="add-exercise-form"
      >
        <input
          type="text"
          value={newExercise}
          onChange={(e) => setNewExercise(e.target.value)}
          placeholder="Add exercise (e.g. Bench Press)"
          data-testid="add-exercise-input"
          className="flex-1 bg-transparent border border-white/15 focus:border-[#FF3333] outline-none px-4 py-3 font-sub"
        />
        <button type="submit" disabled={!newExercise.trim()} data-testid="add-exercise-btn" className="btn-forged-red disabled:opacity-40">
          <Plus className="w-4 h-4" /> Add
        </button>
      </form>
    </div>
  );
}
